import { useEffect, useRef } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from './icons'

type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE_MAX_WIDTH: Record<DialogSize, number> = {
  sm: 380,
  md: 448,
  lg: 640,
  xl: 1180,
}

interface DialogProps extends PropsWithChildren {
  open: boolean
  title: string
  footer?: ReactNode
  onClose: () => void
  onConfirm?: () => void
  size?: DialogSize
  maxWidth?: number
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName.toLowerCase()
  if (['input', 'textarea', 'select'].includes(tag)) return true
  return el.isContentEditable
}

export function Dialog({ open, title, footer, onClose, onConfirm, children, size = 'md', maxWidth }: DialogProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const frameId = window.requestAnimationFrame(() => {
      panelRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [open])

  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    const handleEnter = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || !onConfirm) return
      if (event.defaultPrevented || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return
      if (isTypingTarget(event.target)) return
      event.preventDefault()
      onConfirm()
    }

    window.addEventListener('keydown', handleEscape)
    window.addEventListener('keydown', handleEnter)
    return () => {
      window.removeEventListener('keydown', handleEscape)
      window.removeEventListener('keydown', handleEnter)
    }
  }, [onClose, onConfirm, open])

  if (!open) return null

  return (
    <div data-overlay-root="true" className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4">
      <div
        ref={panelRef}
        tabIndex={-1}
        className="flex w-full flex-col rounded-xl border shadow-2xl"
        style={{
          maxWidth: maxWidth ?? SIZE_MAX_WIDTH[size],
          maxHeight: 'calc(100dvh - 2rem)',
          background: 'var(--bg-modal)',
          borderColor: 'var(--border)',
          color: 'var(--text)',
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--border)' }}>
          <strong className="text-sm">{title}</strong>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
            className="inline-flex h-6 w-6 items-center justify-center text-sm transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm">{children}</div>
        {footer ? (
          <div className="flex shrink-0 justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--border)' }}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}
